import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail, Lock, Eye, EyeOff, ArrowRight,
  CheckCircle, Dumbbell, BookOpen, Sparkles, Target,
  ChevronRight, Zap, Shield,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const DOELEN = [
  { key: "afvallen",         label: "Afvallen",              emoji: "🔥" },
  { key: "spieropbouw",      label: "Spieropbouw",           emoji: "💪" },
  { key: "sterker_worden",   label: "Sterker worden",        emoji: "🏋️" },
  { key: "conditie",         label: "Conditie verbeteren",   emoji: "🏃" },
  { key: "onderhoud",        label: "Onderhoud / Fit blijven", emoji: "⚡" },
];

const NIVEAUS = [
  { key: "beginner",  label: "Beginner",  sub: "0-1 jaar ervaring" },
  { key: "gevorderd", label: "Gevorderd", sub: "1-3 jaar ervaring" },
  { key: "advanced",  label: "Advanced",  sub: "3+ jaar ervaring" },
];

const PRODUCTEN = [
  {
    key: "vrije_training",
    naam: "Vrije Training",
    prijs: 25,
    icon: <Dumbbell size={20} />,
    omschrijving: "Zelf losse trainingen maken en uitvoeren zonder vast schema.",
    badge: "Populair",
  },
  {
    key: "schema_builder_unlock",
    naam: "Eigen Schemamaker",
    prijs: 100,
    icon: <Sparkles size={20} />,
    omschrijving: "Zelf volledige trainingsschema's kunnen maken, opslaan en aanpassen.",
    badge: null,
  },
  {
    key: "persoonlijk_schema",
    naam: "Persoonlijk Trainingsschema",
    prijs: 49.99,
    icon: <Target size={20} />,
    omschrijving: "Een op maat gemaakt schema door jouw coach, afgestemd op jouw doelen en niveau.",
    badge: "Aanbevolen",
  },
  {
    key: "cursus",
    naam: "Cursus Pro",
    prijs: 399.95,
    icon: <BookOpen size={20} />,
    omschrijving: "Volledige online fitnesscursus met lessen, toetsen en certificaat.",
    badge: null,
  },
];

// ── Progress bar ──────────────────────────────────────────────────────────────
function StepBar({ step }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {[1, 2].map((s) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
            s < step  ? "bg-[#C9A84C] border-[#C9A84C] text-black" :
            s === step ? "border-[#C9A84C] text-[#C9A84C] bg-transparent" :
                        "border-zinc-700 text-zinc-600 bg-transparent"
          }`}>
            {s < step ? <CheckCircle size={14} /> : s}
          </div>
          <span className={`text-xs font-semibold ${s === step ? "text-white" : "text-zinc-600"}`}>
            {s === 1 ? "Account" : "Diensten"}
          </span>
          {s < 2 && <div className={`w-8 h-0.5 rounded-full ${step > 1 ? "bg-[#C9A84C]" : "bg-zinc-800"}`} />}
        </div>
      ))}
    </div>
  );
}

// ── Stap 1: Registratieformulier ──────────────────────────────────────────────
function Stap1({ onNext }) {
  const [form, setForm] = useState({
    voornaam: "", achternaam: "", email: "", password: "",
    leeftijd: "", lengte: "", gewicht: "",
    doel: "", niveau: "",
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { loginWithToken } = useAuth();

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.voornaam || !form.achternaam || !form.email || !form.password)
      return setError("Vul naam, e-mail en wachtwoord in");
    if (form.password.length < 8) return setError("Wachtwoord minimaal 8 tekens");
    if (!form.doel) return setError("Kies een trainingsdoel");
    if (!form.niveau) return setError("Kies je trainingsniveau");

    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          voornaam: form.voornaam.trim(),
          achternaam: form.achternaam.trim(),
          email: form.email.trim(),
          password: form.password,
          leeftijd: form.leeftijd ? parseInt(form.leeftijd) : null,
          lengte: form.lengte ? parseFloat(form.lengte) : null,
          gewicht: form.gewicht ? parseFloat(form.gewicht) : null,
          doel: form.doel,
          niveau: form.niveau,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(Array.isArray(d.detail) ? d.detail[0]?.msg : d.detail);
      // Sla token op in localStorage zodat auth context bijgewerkt wordt
      localStorage.setItem("fitsmith_token", d.token);
      loginWithToken(d.user, d.token);
      onNext(d.token, d.user);
    } catch (err) {
      setError(err.message || "Registreren mislukt");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">Maak je account aan</h1>
        <p className="text-zinc-400 text-sm">Stap 1: Jouw gegevens</p>
      </div>

      {/* Naam */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">Voornaam</label>
          <input type="text" value={form.voornaam} onChange={set("voornaam")} placeholder="Jan"
            data-testid="reg-voornaam" required
            className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/50 transition-colors" />
        </div>
        <div>
          <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">Achternaam</label>
          <input type="text" value={form.achternaam} onChange={set("achternaam")} placeholder="de Vries"
            data-testid="reg-achternaam" required
            className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/50 transition-colors" />
        </div>
      </div>

      {/* E-mail */}
      <div>
        <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">E-mailadres</label>
        <div className="relative">
          <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input type="email" value={form.email} onChange={set("email")} placeholder="jan@email.com"
            data-testid="reg-email" required
            className="w-full bg-[#111] border border-white/8 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/50 transition-colors" />
        </div>
      </div>

      {/* Wachtwoord */}
      <div>
        <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">Wachtwoord</label>
        <div className="relative">
          <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input type={showPass ? "text" : "password"} value={form.password} onChange={set("password")}
            placeholder="Minimaal 8 tekens"
            data-testid="reg-password" required
            className="w-full bg-[#111] border border-white/8 rounded-xl pl-10 pr-12 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/50 transition-colors" />
          <button type="button" onClick={() => setShowPass(v => !v)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-300 transition-colors">
            {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
      </div>

      {/* Lichaam stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { k: "leeftijd", label: "Leeftijd", placeholder: "25", unit: "jr", testid: "reg-leeftijd" },
          { k: "lengte",   label: "Lengte",   placeholder: "175", unit: "cm", testid: "reg-lengte" },
          { k: "gewicht",  label: "Gewicht",  placeholder: "75",  unit: "kg", testid: "reg-gewicht" },
        ].map(({ k, label, placeholder, unit, testid }) => (
          <div key={k}>
            <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">{label}</label>
            <div className="relative">
              <input type="number" value={form[k]} onChange={set(k)} placeholder={placeholder}
                data-testid={testid}
                className="w-full bg-[#111] border border-white/8 rounded-xl px-3 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/50 transition-colors pr-8" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 text-xs">{unit}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Doel */}
      <div>
        <label className="block text-zinc-400 text-xs font-semibold mb-2 uppercase tracking-wide">Trainingsdoel</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {DOELEN.map(({ key, label, emoji }) => (
            <button key={key} type="button" onClick={() => setForm(p => ({ ...p, doel: key }))}
              data-testid={`doel-${key}`}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-semibold transition-all text-left ${
                form.doel === key
                  ? "border-[#C9A84C] bg-[#C9A84C]/10 text-white"
                  : "border-white/8 bg-[#111] text-zinc-400 hover:border-white/20 hover:text-white"
              }`}>
              <span>{emoji}</span> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Niveau */}
      <div>
        <label className="block text-zinc-400 text-xs font-semibold mb-2 uppercase tracking-wide">Trainingsniveau</label>
        <div className="grid grid-cols-3 gap-2">
          {NIVEAUS.map(({ key, label, sub }) => (
            <button key={key} type="button" onClick={() => setForm(p => ({ ...p, niveau: key }))}
              data-testid={`niveau-${key}`}
              className={`flex flex-col items-center py-3 px-2 rounded-xl border text-center transition-all ${
                form.niveau === key
                  ? "border-[#C9A84C] bg-[#C9A84C]/10 text-white"
                  : "border-white/8 bg-[#111] text-zinc-400 hover:border-white/20 hover:text-white"
              }`}>
              <span className="text-sm font-bold">{label}</span>
              <span className="text-[10px] opacity-60 mt-0.5">{sub}</span>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/30 text-red-400 text-sm rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      <button type="submit" disabled={loading} data-testid="reg-submit"
        className="w-full py-3.5 rounded-xl font-bold text-sm text-black flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
        style={{ background: "#C9A84C" }}>
        {loading ? "Account aanmaken..." : <><span>Verder naar diensten</span><ArrowRight size={16} /></>}
      </button>

      <p className="text-center text-zinc-600 text-xs">
        Al een account?{" "}
        <a href="/login" className="text-[#C9A84C] hover:underline font-semibold">Inloggen</a>
      </p>
    </form>
  );
}

// ── Stap 2: Diensten kiezen ───────────────────────────────────────────────────
function Stap2({ token, user, onBack }) {
  const navigate = useNavigate();
  const [geselecteerd, setGeselecteerd] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const toggle = (key) => setGeselecteerd((p) => ({ ...p, [key]: !p[key] }));

  const totaal = PRODUCTEN.filter((p) => geselecteerd[p.key]).reduce((s, p) => s + p.prijs, 0);
  const geselecteerdeItems = PRODUCTEN.filter((p) => geselecteerd[p.key]);

  const handleBetalen = async () => {
    if (geselecteerdeItems.length === 0) {
      navigate("/account");
      return;
    }
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/payments/bundle-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          items: geselecteerdeItems.map((p) => ({ product: p.key })),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Betaling aanmaken mislukt");
      window.location.href = d.checkout_url;
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">Kies je diensten</h1>
        <p className="text-zinc-400 text-sm">Stap 2: Selecteer wat bij jou past. Je kunt dit later altijd uitbreiden.</p>
      </div>

      {/* Producten */}
      <div className="space-y-3">
        {PRODUCTEN.map((product) => {
          const aan = geselecteerd[product.key];
          return (
            <button key={product.key} type="button"
              onClick={() => toggle(product.key)}
              data-testid={`product-${product.key}`}
              className={`w-full flex items-start gap-4 p-4 rounded-2xl border text-left transition-all ${
                aan
                  ? "border-[#C9A84C] bg-[#C9A84C]/8"
                  : "border-white/8 bg-[#111] hover:border-white/20"
              }`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                aan ? "bg-[#C9A84C]/20 text-[#C9A84C]" : "bg-white/5 text-zinc-500"
              }`}>
                {product.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-bold text-sm">{product.naam}</span>
                  {product.badge && (
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#C9A84C]/15 text-[#C9A84C] border border-[#C9A84C]/25">
                      {product.badge}
                    </span>
                  )}
                </div>
                <p className="text-zinc-500 text-xs mt-0.5 leading-relaxed">{product.omschrijving}</p>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className="text-white font-bold text-sm">
                  €{product.prijs % 1 === 0 ? product.prijs : product.prijs.toFixed(2).replace(".", ",")}
                </span>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                  aan ? "border-[#C9A84C] bg-[#C9A84C]" : "border-zinc-600"
                }`}>
                  {aan && <CheckCircle size={12} className="text-black fill-black" />}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Totaal overzicht */}
      {geselecteerdeItems.length > 0 && (
        <div className="bg-[#0a0a0a] border border-white/8 rounded-2xl p-4 space-y-2">
          <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-3">Jouw selectie</p>
          {geselecteerdeItems.map((p) => (
            <div key={p.key} className="flex items-center justify-between text-sm">
              <span className="text-zinc-300">{p.naam}</span>
              <span className="text-white font-semibold">€{p.prijs % 1 === 0 ? p.prijs : p.prijs.toFixed(2).replace(".", ",")}</span>
            </div>
          ))}
          <div className="border-t border-white/8 pt-2 flex items-center justify-between">
            <span className="text-white font-bold">Totaal</span>
            <span className="text-[#C9A84C] font-bold text-lg">
              €{totaal % 1 === 0 ? totaal : totaal.toFixed(2).replace(".", ",")}
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-900/20 border border-red-500/30 text-red-400 text-sm rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {/* Knoppen */}
      <button onClick={handleBetalen} disabled={loading || geselecteerdeItems.length === 0} data-testid="naar-betaling-btn"
        className="w-full py-3.5 rounded-xl font-bold text-sm text-black flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
        style={{ background: "#C9A84C" }}>
        {loading
          ? "Laden..."
          : <><span>Doorgaan naar betaling</span><ChevronRight size={16} /></>
        }
      </button>

      {/* Altijd zichtbare overslaan-knop */}
      <button
        onClick={() => navigate("/account")}
        data-testid="skip-diensten-btn"
        className="w-full py-3 rounded-xl font-semibold text-sm border border-white/10 bg-white/3 text-zinc-400 hover:text-white hover:border-white/20 transition-all flex items-center justify-center gap-2"
      >
        <ArrowRight size={14} className="opacity-50" />
        Nee, ik kies later — leeg account aanmaken
      </button>
    </div>
  );
}

// ── Hoofd pagina ──────────────────────────────────────────────────────────────
const HERO_IMG = "https://images.pexels.com/photos/6388373/pexels-photo-6388373.jpeg";
const LOGO = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";

export default function Registreren() {
  const [stap, setStap] = useState(1);
  const [token, setToken] = useState(null);
  const [regUser, setRegUser] = useState(null);
  const navigate = useNavigate();

  const handleStap1Klaar = (tok, usr) => {
    setToken(tok);
    setRegUser(usr);
    setStap(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#080808] flex">
      {/* Links: hero image (alleen desktop) */}
      <div className="hidden lg:flex lg:w-[45%] relative flex-col justify-between p-10 overflow-hidden">
        <img src={HERO_IMG} alt="FitSmith" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080808]/60 to-[#080808]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-transparent" />
        <div className="relative z-10 flex items-center gap-3">
          <img src={LOGO} alt="FitSmith" className="h-9 w-9 object-contain" />
          <span className="text-white font-bold text-lg">FitSmith</span>
        </div>
        <div className="relative z-10 space-y-4">
          <h2 className="text-3xl font-bold text-white leading-tight">
            Start jouw<br />
            <span style={{ color: "#C9A84C" }}>transformatie</span><br />
            vandaag nog.
          </h2>
          <div className="space-y-2">
            {[
              { icon: <Zap size={14} />, text: "Direct toegang na betaling" },
              { icon: <Shield size={14} />, text: "Veilig betalen via iDEAL" },
              { icon: <CheckCircle size={14} />, text: "Altijd toegang tot je aankopen" },
            ].map(({ icon, text }) => (
              <div key={text} className="flex items-center gap-2 text-zinc-300 text-sm">
                <span style={{ color: "#C9A84C" }}>{icon}</span> {text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rechts: formulier */}
      <div className="flex-1 flex flex-col items-center justify-start py-10 px-5 sm:px-8 overflow-y-auto">
        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-2 mb-8 self-start">
          <img src={LOGO} alt="FitSmith" className="h-8 w-8 object-contain" />
          <span className="text-white font-bold">FitSmith</span>
        </div>

        <div className="w-full max-w-[480px]">
          <StepBar step={stap} />

          {stap === 1 && <Stap1 onNext={handleStap1Klaar} />}
          {stap === 2 && (
            <Stap2
              token={token}
              user={regUser}
              onBack={() => setStap(1)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
